import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Pagination, Typography } from "@mui/material";
import { uniq } from "lodash";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getVetsSpecialityReportData } from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./VetSpecialties.css";

const tableHdrs = ["vetName", "speciality"];
const initSearch = { vetName: "", speciality: [] };

const VetsSpecialityReport = () => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs);
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const { vetSpeciality } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    onApiCall(1);
  }, [searchValues?.vetName, searchValues?.speciality]);

  const getReportDataList = (vetSpec) => {
    const payload = vetSpec?.data || vetSpec;
    if (Array.isArray(payload?.roster)) return payload.roster;
    if (Array.isArray(payload?.vets)) return payload.vets;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.bySpecialty)) return payload.bySpecialty;
    if (Array.isArray(payload?.specialtyBreakdown)) return payload.specialtyBreakdown;
    if (Array.isArray(payload)) return payload;
    return [];
  };

  const reportData = vetSpeciality?.data || vetSpeciality || {};
  const summaryInfo = reportData?.summary || {};

  const tableDataList = getReportDataList(vetSpeciality);
  const totalRecords =
    reportData?.totalRecords ??
    reportData?.totalCount ??
    vetSpeciality?.totalRecords ??
    tableDataList.length;
  const totalPage = Math.ceil(totalRecords / rowsPerPage);

  useEffect(() => {
    if (tableDataList.length > 0) {
      const firstItem = tableDataList[0];
      if (firstItem?.specialty && firstItem?.vetCount !== undefined && !firstItem?.vetName) {
        setCrntTblHeders(["specialty", "vetCount", "percentageDisplay"]);
      } else if (firstItem?.vetName) {
        setCrntTblHeders(["vetName", "speciality"]);
      }
    }
  }, [vetSpeciality]);

  const summaryCards = [
    {
      title: "Active vets",
      value:
        summaryInfo?.activeVets ??
        reportData?.activeVets ??
        vetSpeciality?.activeVets ??
        (tableDataList.length > 0 ? tableDataList.length : "0"),
    },
    {
      title: "Primary specialties",
      value:
        summaryInfo?.primarySpecialties ??
        reportData?.primarySpecialties ??
        vetSpeciality?.primarySpecialties ??
        (reportData?.bySpecialty ? reportData.bySpecialty.length : "0"),
    },
  ];

  const chartBreakdown =
    reportData?.bySpecialty ||
    reportData?.specialtyBreakdown ||
    reportData?.specialities ||
    reportData?.chartData ||
    [];

  let chartLabels = ["Surgery", "General Medicine", "Oncology", "Anesthesiology"];
  let donutChartSeries = [3, 3, 1, 1];

  if (Array.isArray(chartBreakdown) && chartBreakdown.length > 0) {
    chartLabels = chartBreakdown.map(
      (item) => item?.specialty || item?.label || item?.name || "",
    );
    donutChartSeries = chartBreakdown.map((item) =>
      Number(item?.vetCount ?? item?.count ?? item?.value ?? 0),
    );
  } else if (reportData?.chartData?.labels && reportData?.chartData?.series) {
    chartLabels = reportData.chartData.labels;
    donutChartSeries = reportData.chartData.series;
  }

  const chartTitle =
    reportData?.chartTitle ||
    "Vets by primary specialty (click a slice to filter table)";

  const donutChartOptions = {
    chart: {
      type: "donut",
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const selectedIndex = config.dataPointIndex;
          if (selectedIndex >= 0 && chartLabels[selectedIndex]) {
            const spec = chartLabels[selectedIndex];
            setSearchValues((prev) => ({
              ...prev,
              speciality: [{ name: spec, value: spec }],
            }));
          }
        },
      },
    },
    labels: chartLabels,
    colors: [
      "#1976D2", "#E65100", "#10B981", "#FFC225", "#9C27B0", "#FF9800",
      "#00BCD4", "#E91E63", "#3F51B5", "#8BC34A", "#009688", "#795548",
    ],
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontFamily: "Montserrat, sans-serif",
      fontSize: "13px",
      markers: {
        width: 10,
        height: 10,
        radius: 2,
      },
    },
    grid: {
      padding: {
        top: 0,
        bottom: 0,
      },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} vets`,
      },
    },
  };

  const onApiCall = (pge = 1, reset = false) => {
    setPage(pge);
    const reqObj = { page: pge, itemSize: rowsPerPage };
    if (reset) return reqObj;
    if (searchValues?.vetName?.length > 0) {
      reqObj.vetName = searchValues?.vetName;
    }
    if (searchValues?.speciality?.length > 0) {
      reqObj.speciality = searchValues?.speciality?.map((li) => li?.value);
    }
    dispatch(getVetsSpecialityReportData(reqObj));
  };

  const getVetList = () => {
    const reqList = tableDataList
      ?.map((li) => li?.vetName || li?.name || li?.doctorName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getSpecialityList = () => {
    const reqList = tableDataList
      ?.flatMap((li) => {
        if (Array.isArray(li?.speciality)) return li.speciality;
        if (typeof li?.speciality === "string") return [li.speciality];
        if (Array.isArray(li?.specialties)) return li.specialties;
        if (typeof li?.specialties === "string") return [li.specialties];
        if (typeof li?.primarySpecialty === "string") return [li.primarySpecialty];
        return [];
      })
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value,
    });
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    onApiCall(1, true);
  };

  return (
    <div className="vet-specialties-container">
      {/* Page Heading */}
      <h2 className="vet-specialties-title">Vet specialties</h2>

      {/* Summary Cards */}
      <div className="vet-specialties-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="vet-specialties-card" key={idx}>
            <div className="vet-specialties-card-title">{card.title}</div>
            <div className="vet-specialties-card-value">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Chart Section */}
      <div className="vet-specialties-chart-grid">
        <div className="vet-specialties-chart-card">
          <div className="vet-specialties-chart-header">
            {chartTitle}
          </div>
          <Chart
            options={donutChartOptions}
            series={donutChartSeries}
            type="donut"
            height={280}
          />
        </div>
      </div>

      {/* Drill-down — roster Section Header */}
      <div className="vet-specialties-drilldown-header">
        Drill-down — roster
      </div>

      {/* Search Filters Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getVetList()}
              value={searchValues?.vetName ?? ""}
              name="vetName"
              handleChange={onSearchChange}
              label="Vet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={getSpecialityList()}
              value={searchValues?.speciality ?? []}
              name="speciality"
              handleChange={onSearchChange}
              label="Speciality"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {totalRecords}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={tableDataList}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {totalPage > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default VetsSpecialityReport;
